import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Box,
  Flex,
  Image,
  Select,
  Spinner,
  Text,
  Tooltip,
  useDisclosure
} from '@chakra-ui/react';
import mime from 'mime-types';
import { useSearchParams } from 'react-router-dom';
import s3Utils from '@/utils/s3Utils';
import { LicenseReview, LOAReview, SOTTaxStamps } from '@/model/license-review.mjs';
import LicenseReviewDataSource from '../ducks/licenseReviewDatasource';
import LOATaxStampsDatasource from '../ducks/LOATaxStampsDatasource';
import SOTTaxStampsDataSource from '../ducks/SOTTaxStampsDatasource';
import {
  getHideUnnecessaryLOARejectionValue,
  getHideUnnecessaryRejectionValue,
  getHideUnnecessarySOTRejectionValue,
  getLicenseApprovalFilterModel,
  getLicenseApprovalSort,
  getLOAApprovalFilterModel,
  getLOAApprovalSort,
  getSOTApprovalFilterModel,
  getSOTApprovalSort
} from './selector';
import { SERVER_PAGE_LICENSE_APPROVAL_GALLERY_SIZE } from '@/model/Constants';

/** Matches AG Grid `overlayNoRowsTemplate` helper line (U+2019 apostrophe). */
const GALLERY_EMPTY_FILTER_HINT =
  'Try adjusting your filter to find what you\u2019re looking for';

/** Titles match AG Grid `overlayNoRowsTemplate` for consistent empty states. */
export const LICENSE_GALLERY_EMPTY_TITLE = 'No License Approval found';
export const LOA_GALLERY_EMPTY_TITLE = 'No LOA found';
export const SOT_GALLERY_EMPTY_TITLE = 'No SOT Tax Stamps found';

/** Thumbnail width range; height follows aspect-ratio 63/82 (width:height). Max ~572.781×745.52px. */
const THUMB_MIN_W = 230;
const THUMB_MAX_W = 572.781;
const THUMB_ASPECT_W = 63;
const THUMB_ASPECT_H = 82;

const THUMBNAIL_DISPLAY_ORIGIN =
  (import.meta.env.VITE_FFL360_DOMAIN as string | undefined)?.replace(
    /\/$/,
    ''
  ) || 'https://ffl360-qa.masterffl.com';

/** Replace localhost in absolute http(s) URLs only (blob: URLs must stay unchanged). */
function thumbnailSrcForDisplay(raw: string | null): string | undefined {
  if (!raw) return undefined;
  if (/^blob:/i.test(raw)) return raw;
  try {
    const u = new URL(raw);
    if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') {
      return `${THUMBNAIL_DISPLAY_ORIGIN}${u.pathname}${u.search}`;
    }
  } catch {
    return raw;
  }
  return raw;
}

export const DOCUMENTS_GRID_PAGE_SIZE_OPTIONS = [
  30,
  50,
  100
] as const;

/** Cap parallel S3 thumbnail downloads (avoids saturating network / main thread). */
const THUMB_FETCH_CONCURRENCY = 6;

function createConcurrencyLimiter(maxConcurrent: number) {
  let inFlight = 0;
  const queue: Array<{
    fn: () => Promise<unknown>;
    resolve: (v: unknown) => void;
    reject: (e: unknown) => void;
  }> = [];

  const runNext = () => {
    while (inFlight < maxConcurrent && queue.length > 0) {
      const job = queue.shift()!;
      inFlight++;
      job
        .fn()
        .then(job.resolve)
        .catch(job.reject)
        .finally(() => {
          inFlight--;
          runNext();
        });
    }
  };

  return function limit<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      queue.push({
        fn,
        resolve: resolve as (v: unknown) => void,
        reject
      });
      runNext();
    });
  };
}

const limitThumbS3Fetch = createConcurrencyLimiter(THUMB_FETCH_CONCURRENCY);

/** After `load`, wait for `decode()` when supported so the visible `<Image>` does not flash on swap. */
function runAfterImageDecode(img: HTMLImageElement, onReady: () => void): void {
  const dec = img.decode?.();
  if (dec) {
    dec.then(onReady).catch(onReady);
  } else {
    onReady();
  }
}

/** True if bytes start with the PDF file signature (filters HTML/XML error bodies, empty files). */
async function pdfBlobHasPdfHeader(blob: Blob): Promise<boolean> {
  if (blob.size < 5) {
    return false;
  }
  const buf = await blob.slice(0, 5).arrayBuffer();
  const prefix = new TextDecoder('latin1').decode(buf);
  return prefix.startsWith('%PDF-');
}

function lerp(min: number, max: number, t: number) {
  return min + (max - min) * t;
}

/** Document-with-fold icon for empty / non-PDF error thumbnail states (Figma). */
export function DocumentPlaceholderIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={52}
      height={52}
      viewBox="0 0 52 52"
      fill="none"
      aria-hidden
    >
      <path
        d="M13.0003 47.6664C11.8511 47.6664 10.7489 47.2098 9.9362 46.3971C9.12354 45.5845 8.66699 44.4823 8.66699 43.333V8.66635C8.66699 7.51708 9.12354 6.41488 9.9362 5.60222C10.7489 4.78956 11.8511 4.33301 13.0003 4.33301H30.3337C31.0195 4.3319 31.6988 4.46649 32.3325 4.72901C32.9661 4.99154 33.5416 5.37683 34.0257 5.86268L41.7997 13.6367C42.2868 14.1209 42.6732 14.6969 42.9365 15.3314C43.1998 15.9658 43.3348 16.6461 43.3337 17.333V43.333C43.3337 44.4823 42.8771 45.5845 42.0645 46.3971C41.2518 47.2098 40.1496 47.6664 39.0003 47.6664H13.0003Z"
        stroke="#0C1E46"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M30.333 4.33301V15.1663C30.333 15.741 30.5613 16.2921 30.9676 16.6984C31.3739 17.1047 31.925 17.333 32.4997 17.333H43.333"
        stroke="#0C1E46"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** PDF document placeholder for grid cards (embedded browser PDF UI is not used — see pdfBlobHasPdfHeader / pdfFetchOk). */
export function DocumentPdfPlaceholderIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={52}
      height={52}
      viewBox="0 0 52 52"
      fill="none"
      aria-hidden
    >
      <path
        d="M13.0003 47.6664C11.8511 47.6664 10.7489 47.2098 9.9362 46.3971C9.12354 45.5845 8.66699 44.4823 8.66699 43.333V8.66635C8.66699 7.51708 9.12354 6.41488 9.9362 5.60222C10.7489 4.78956 11.8511 4.33301 13.0003 4.33301H30.3337C31.0195 4.3319 31.6988 4.46649 32.3325 4.72901C32.9661 4.99154 33.5416 5.37683 34.0257 5.86268L41.7997 13.6367C42.2868 14.1209 42.6732 14.6969 42.9365 15.3314C43.1998 15.9658 43.3348 16.6461 43.3337 17.333V43.333C43.3337 44.4823 42.8771 45.5845 42.0645 46.3971C41.2518 47.2098 40.1496 47.6664 39.0003 47.6664H13.0003Z"
        stroke="#0C1E46"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M30.333 4.33301V15.1663C30.333 15.741 30.5613 16.2921 30.9676 16.6984C31.3739 17.1047 31.925 17.333 32.4997 17.333H43.333"
        stroke="#0C1E46"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.4506 32.3265C17.7713 32.3265 18.0508 32.2875 18.2891 32.2095C18.5275 32.1272 18.7246 32.0123 18.8806 31.865C19.041 31.7133 19.1601 31.5313 19.2381 31.319C19.3161 31.1023 19.3551 30.8618 19.3551 30.5975C19.3551 30.3462 19.3161 30.1187 19.2381 29.915C19.1601 29.7113 19.0431 29.538 18.8871 29.395C18.7311 29.252 18.534 29.1437 18.2956 29.07C18.0573 28.992 17.7756 28.953 17.4506 28.953H16.1441V32.3265H17.4506ZM17.4506 27.601C18.079 27.601 18.6228 27.6747 19.0821 27.822C19.5415 27.9693 19.9206 28.1752 20.2196 28.4395C20.5186 28.7038 20.7396 29.0202 20.8826 29.3885C21.03 29.7568 21.1036 30.1598 21.1036 30.5975C21.1036 31.0525 21.0278 31.4707 20.8761 31.852C20.7245 32.229 20.497 32.554 20.1936 32.827C19.8903 33.1 19.509 33.3123 19.0496 33.464C18.5946 33.6157 18.0616 33.6915 17.4506 33.6915H16.1441V37H14.3956V27.601H17.4506ZM30.7904 32.3005C30.7904 32.9895 30.6756 33.6222 30.4459 34.1985C30.2163 34.7748 29.8934 35.271 29.4774 35.687C29.0614 36.103 28.5609 36.4258 27.9759 36.6555C27.3909 36.8852 26.7409 37 26.0259 37H22.4444V27.601H26.0259C26.7409 27.601 27.3909 27.718 27.9759 27.952C28.5609 28.1817 29.0614 28.5045 29.4774 28.9205C29.8934 29.3322 30.2163 29.8262 30.4459 30.4025C30.6756 30.9788 30.7904 31.6115 30.7904 32.3005ZM28.9964 32.3005C28.9964 31.7848 28.9271 31.3233 28.7884 30.916C28.6541 30.5043 28.4569 30.1577 28.1969 29.876C27.9413 29.59 27.6293 29.3712 27.2609 29.2195C26.8969 29.0678 26.4853 28.992 26.0259 28.992H24.1994V35.609H26.0259C26.4853 35.609 26.8969 35.5332 27.2609 35.3815C27.6293 35.2298 27.9413 35.0132 28.1969 34.7315C28.4569 34.4455 28.6541 34.0988 28.7884 33.6915C28.9271 33.2798 28.9964 32.8162 28.9964 32.3005ZM34.0891 28.992V31.774H37.6121V33.1715H34.0891V37H32.3341V27.601H38.2621V28.992H34.0891Z"
        fill="#0C1E46"
      />
    </svg>
  );
}

const PLACEHOLDER_INNER_SX = {
  bg: '#D7EBF9',
};

const DOC_GRID_STOP_CLICK = 'data-doc-grid-stop-click';

const DOC_CARD_OVERLAY_FONT = "'Lato', sans-serif";

/** Top-right gallery card status chip (same layout as Pending). */
export type DocumentCardStatusBadgeKind =
  | 'pending'
  | 'rejected'
  | 'approved'
  | 'reviewed';

const DOC_CARD_STATUS_BADGE_STYLES: Record<
  DocumentCardStatusBadgeKind,
  { bg: string; color: string; label: string }
> = {
  pending: { bg: '#FEF9C2', color: '#4C2A00', label: 'Pending' },
  rejected: { bg: '#FEE2E2', color: '#991B1B', label: 'Rejected' },
  approved: { bg: '#DCFCE7', color: '#166534', label: 'Approved' },
  reviewed: { bg: '#E2EBFF', color: '#0C1E46', label: 'Reviewed' }
};

function DocumentCardStatusBadge({ kind }: { kind: DocumentCardStatusBadgeKind }) {
  const { bg, color, label } = DOC_CARD_STATUS_BADGE_STYLES[kind];
  return (
    <Flex
      className="documents-grid-view__badge"
      position="absolute"
      top="10px"
      right="10px"
      zIndex={2}
      h="24px"
      minW="60.594px"
      px="8px"
      py="4px"
      alignItems="center"
      justifyContent="center"
      borderRadius="4px"
      bg={bg}
      color={color}
      fontFamily="'Inter', sans-serif"
      fontSize="12px"
      fontStyle="normal"
      fontWeight="500"
      lineHeight="16px"
    >
      {label}
    </Flex>
  );
}

/** Rotate / Reject on card hover — Figma min widths; shared padding */
const DOC_CARD_HOVER_ACTION_MIN_W = '77px';
/** Figma: smallest thumbnail row — action controls use this floor before capping to column width */
const DOC_CARD_HOVER_ACTION_MIN_W_SMALL = '77px';
const DOC_CARD_HOVER_ACTION_PADDING = '8px';

/** Card hover scrim — Figma */
const DOC_CARD_HOVER_OVERLAY_BG =
  'linear-gradient(180deg, #0C1E46 0%, rgba(12, 30, 70, 0.00) 150%)';

/** Below ~288px card width, Figma uses 94px min; wider cards use 118.067px. Always cap by column: min(..., 100%). */
function docCardHoverActionMinForFigma(cardWidthPx: number): string {
  return cardWidthPx <= 288 ? DOC_CARD_HOVER_ACTION_MIN_W_SMALL : DOC_CARD_HOVER_ACTION_MIN_W;
}

function docCardHoverActionMinClipExpr(cardWidthPx: number): string {
  return `min(${docCardHoverActionMinForFigma(cardWidthPx)}, 100%)`;
}

function DocumentCardRotateIcon() {
  return (
    <Box as="span" display="inline-flex" flexShrink={0} aria-hidden>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12.25 7C12.25 8.03835 11.9421 9.05339 11.3652 9.91674C10.7883 10.7801 9.9684 11.453 9.00909 11.8504C8.04978 12.2477 6.99418 12.3517 5.97578 12.1491C4.95738 11.9466 4.02192 11.4465 3.28769 10.7123C2.55347 9.97808 2.05345 9.04262 1.85088 8.02422C1.64831 7.00582 1.75227 5.95022 2.14963 4.99091C2.54699 4.0316 3.2199 3.21166 4.08326 2.63478C4.94662 2.05791 5.96165 1.75 7 1.75C8.47 1.75 9.87584 2.33333 10.9317 3.34833L12.25 4.66667"
          stroke="#0C1E46"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.2497 1.75V4.66667H9.33301"
          stroke="#0C1E46"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
}

export type DocumentGridRotateKind = 'cw90' | 'ccw90' | 'deg180';

/** License gallery: outline control (pending) vs filled indicator (reviewed). */
export type DocumentCardHoverReviewKind = 'action' | 'done';

const ROTATE_MENU_NAVY = '#0C1E46';

function RotateMenuIconCw90() {
  return (
    <Box as="span" display="inline-flex" flexShrink={0} aria-hidden>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12.25 7C12.25 8.03835 11.9421 9.05339 11.3652 9.91674C10.7883 10.7801 9.9684 11.453 9.00909 11.8504C8.04978 12.2477 6.99418 12.3517 5.97578 12.1491C4.95738 11.9466 4.02192 11.4465 3.28769 10.7123C2.55347 9.97808 2.05345 9.04262 1.85088 8.02422C1.64831 7.00582 1.75227 5.95022 2.14963 4.99091C2.54699 4.0316 3.2199 3.21166 4.08326 2.63478C4.94662 2.05791 5.96165 1.75 7 1.75C8.47 1.75 9.87584 2.33333 10.9317 3.34833L12.25 4.66667"
          stroke={ROTATE_MENU_NAVY}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.2497 1.75V4.66667H9.33301"
          stroke={ROTATE_MENU_NAVY}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.13215 6.723C5.13215 6.9045 5.17865 7.04325 5.27165 7.13925C5.36615 7.23375 5.49965 7.281 5.67215 7.281C5.76215 7.281 5.8424 7.26675 5.9129 7.23825C5.9834 7.20975 6.04265 7.17075 6.09065 7.12125C6.13865 7.07175 6.17465 7.01325 6.19865 6.94575C6.22415 6.87825 6.2369 6.8055 6.2369 6.7275C6.2369 6.639 6.2234 6.56025 6.1964 6.49125C6.1694 6.42075 6.13115 6.3615 6.08165 6.3135C6.03215 6.2655 5.9729 6.22875 5.9039 6.20325C5.8364 6.17775 5.76215 6.165 5.68115 6.165C5.59715 6.165 5.5214 6.17925 5.4539 6.20775C5.3864 6.23475 5.32865 6.273 5.28065 6.3225C5.23265 6.372 5.1959 6.43125 5.1704 6.50025C5.1449 6.56775 5.13215 6.642 5.13215 6.723ZM5.87015 7.73775C5.89565 7.70625 5.91965 7.6755 5.94215 7.6455C5.96465 7.6155 5.9864 7.5855 6.0074 7.5555C5.9324 7.6035 5.8499 7.64025 5.7599 7.66575C5.6699 7.68975 5.5754 7.70175 5.4764 7.70175C5.3639 7.70175 5.25365 7.68225 5.14565 7.64325C5.03765 7.60425 4.94165 7.54575 4.85765 7.46775C4.77365 7.38825 4.7054 7.29075 4.6529 7.17525C4.6019 7.05825 4.5764 6.92175 4.5764 6.76575C4.5764 6.62025 4.6034 6.48375 4.6574 6.35625C4.7114 6.22875 4.78715 6.117 4.88465 6.021C4.98215 5.925 5.09915 5.84925 5.23565 5.79375C5.37215 5.73825 5.5229 5.7105 5.6879 5.7105C5.8544 5.7105 6.00365 5.73675 6.13565 5.78925C6.26915 5.84175 6.3824 5.91525 6.4754 6.00975C6.5699 6.10425 6.6419 6.2175 6.6914 6.3495C6.7424 6.4815 6.7679 6.62625 6.7679 6.78375C6.7679 6.88425 6.7589 6.9795 6.7409 7.0695C6.7244 7.1595 6.7004 7.2465 6.6689 7.3305C6.6374 7.413 6.59915 7.494 6.55415 7.5735C6.50915 7.653 6.4589 7.73175 6.4034 7.80975L5.65865 8.87175C5.63315 8.90775 5.5949 8.9385 5.5439 8.964C5.4929 8.988 5.43515 9 5.37065 9H4.8599L5.87015 7.73775ZM9.4975 7.37325C9.4975 7.65675 9.46675 7.9035 9.40525 8.1135C9.34525 8.322 9.26125 8.4945 9.15325 8.631C9.04675 8.7675 8.92 8.8695 8.773 8.937C8.6275 9.003 8.47 9.036 8.3005 9.036C8.131 9.036 7.9735 9.003 7.828 8.937C7.684 8.8695 7.55875 8.7675 7.45225 8.631C7.34575 8.4945 7.2625 8.322 7.2025 8.1135C7.1425 7.9035 7.1125 7.65675 7.1125 7.37325C7.1125 7.08825 7.1425 6.8415 7.2025 6.633C7.2625 6.4245 7.34575 6.252 7.45225 6.1155C7.55875 5.979 7.684 5.87775 7.828 5.81175C7.9735 5.74425 8.131 5.7105 8.3005 5.7105C8.47 5.7105 8.6275 5.74425 8.773 5.81175C8.92 5.87775 9.04675 5.979 9.15325 6.1155C9.26125 6.252 9.34525 6.4245 9.40525 6.633C9.46675 6.8415 9.4975 7.08825 9.4975 7.37325ZM8.92375 7.37325C8.92375 7.13775 8.9065 6.94275 8.872 6.78825C8.8375 6.63375 8.791 6.51075 8.7325 6.41925C8.6755 6.32775 8.6095 6.264 8.5345 6.228C8.4595 6.1905 8.3815 6.17175 8.3005 6.17175C8.221 6.17175 8.14375 6.1905 8.06875 6.228C7.99525 6.264 7.93 6.32775 7.873 6.41925C7.816 6.51075 7.77025 6.63375 7.73575 6.78825C7.70275 6.94275 7.68625 7.13775 7.68625 7.37325C7.68625 7.60875 7.70275 7.80375 7.73575 7.95825C7.77025 8.11275 7.816 8.23575 7.873 8.32725C7.93 8.41875 7.99525 8.48325 8.06875 8.52075C8.14375 8.55675 8.221 8.57475 8.3005 8.57475C8.3815 8.57475 8.4595 8.55675 8.5345 8.52075C8.6095 8.48325 8.6755 8.41875 8.7325 8.32725C8.791 8.23575 8.8375 8.11275 8.872 7.95825C8.9065 7.80375 8.92375 7.60875 8.92375 7.37325Z"
          fill="black"
        />
      </svg>
    </Box>
  );
}

function RotateMenuIconCcw90() {
  return (
    <Box as="span" display="inline-flex" flexShrink={0} aria-hidden>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M1.75 7C1.75 8.03835 2.05791 9.05339 2.63478 9.91674C3.21166 10.7801 4.0316 11.453 4.99091 11.8504C5.95022 12.2477 7.00582 12.3517 8.02422 12.1491C9.04262 11.9466 9.97808 11.4465 10.7123 10.7123C11.4465 9.97808 11.9465 9.04262 12.1491 8.02422C12.3517 7.00582 12.2477 5.95022 11.8504 4.99091C11.453 4.0316 10.7801 3.21166 9.91674 2.63478C9.05338 2.05791 8.03835 1.75 7 1.75C5.53 1.75 4.12416 2.33333 3.06833 3.34833L1.75 4.66667"
          stroke={ROTATE_MENU_NAVY}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M1.75033 1.75V4.66667H4.66699"
          stroke={ROTATE_MENU_NAVY}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.13215 6.723C5.13215 6.9045 5.17865 7.04325 5.27165 7.13925C5.36615 7.23375 5.49965 7.281 5.67215 7.281C5.76215 7.281 5.8424 7.26675 5.9129 7.23825C5.9834 7.20975 6.04265 7.17075 6.09065 7.12125C6.13865 7.07175 6.17465 7.01325 6.19865 6.94575C6.22415 6.87825 6.2369 6.8055 6.2369 6.7275C6.2369 6.639 6.2234 6.56025 6.1964 6.49125C6.1694 6.42075 6.13115 6.3615 6.08165 6.3135C6.03215 6.2655 5.9729 6.22875 5.9039 6.20325C5.8364 6.17775 5.76215 6.165 5.68115 6.165C5.59715 6.165 5.5214 6.17925 5.4539 6.20775C5.3864 6.23475 5.32865 6.273 5.28065 6.3225C5.23265 6.372 5.1959 6.43125 5.1704 6.50025C5.1449 6.56775 5.13215 6.642 5.13215 6.723ZM5.87015 7.73775C5.89565 7.70625 5.91965 7.6755 5.94215 7.6455C5.96465 7.6155 5.9864 7.5855 6.0074 7.5555C5.9324 7.6035 5.8499 7.64025 5.7599 7.66575C5.6699 7.68975 5.5754 7.70175 5.4764 7.70175C5.3639 7.70175 5.25365 7.68225 5.14565 7.64325C5.03765 7.60425 4.94165 7.54575 4.85765 7.46775C4.77365 7.38825 4.7054 7.29075 4.6529 7.17525C4.6019 7.05825 4.5764 6.92175 4.5764 6.76575C4.5764 6.62025 4.6034 6.48375 4.6574 6.35625C4.7114 6.22875 4.78715 6.117 4.88465 6.021C4.98215 5.925 5.09915 5.84925 5.23565 5.79375C5.37215 5.73825 5.5229 5.7105 5.6879 5.7105C5.8544 5.7105 6.00365 5.73675 6.13565 5.78925C6.26915 5.84175 6.3824 5.91525 6.4754 6.00975C6.5699 6.10425 6.6419 6.2175 6.6914 6.3495C6.7424 6.4815 6.7679 6.62625 6.7679 6.78375C6.7679 6.88425 6.7589 6.9795 6.7409 7.0695C6.7244 7.1595 6.7004 7.2465 6.6689 7.3305C6.6374 7.413 6.59915 7.494 6.55415 7.5735C6.50915 7.653 6.4589 7.73175 6.4034 7.80975L5.65865 8.87175C5.63315 8.90775 5.5949 8.9385 5.5439 8.964C5.4929 8.988 5.43515 9 5.37065 9H4.8599L5.87015 7.73775ZM9.4975 7.37325C9.4975 7.65675 9.46675 7.9035 9.40525 8.1135C9.34525 8.322 9.26125 8.4945 9.15325 8.631C9.04675 8.7675 8.92 8.8695 8.773 8.937C8.6275 9.003 8.47 9.036 8.3005 9.036C8.131 9.036 7.9735 9.003 7.828 8.937C7.684 8.8695 7.55875 8.7675 7.45225 8.631C7.34575 8.4945 7.2625 8.322 7.2025 8.1135C7.1425 7.9035 7.1125 7.65675 7.1125 7.37325C7.1125 7.08825 7.1425 6.8415 7.2025 6.633C7.2625 6.4245 7.34575 6.252 7.45225 6.1155C7.55875 5.979 7.684 5.87775 7.828 5.81175C7.9735 5.74425 8.131 5.7105 8.3005 5.7105C8.47 5.7105 8.6275 5.74425 8.773 5.81175C8.92 5.87775 9.04675 5.979 9.15325 6.1155C9.26125 6.252 9.34525 6.4245 9.40525 6.633C9.46675 6.8415 9.4975 7.08825 9.4975 7.37325ZM8.92375 7.37325C8.92375 7.13775 8.9065 6.94275 8.872 6.78825C8.8375 6.63375 8.791 6.51075 8.7325 6.41925C8.6755 6.32775 8.6095 6.264 8.5345 6.228C8.4595 6.1905 8.3815 6.17175 8.3005 6.17175C8.221 6.17175 8.14375 6.1905 8.06875 6.228C7.99525 6.264 7.93 6.32775 7.873 6.41925C7.816 6.51075 7.77025 6.63375 7.73575 6.78825C7.70275 6.94275 7.68625 7.13775 7.68625 7.37325C7.68625 7.60875 7.70275 7.80375 7.73575 7.95825C7.77025 8.11275 7.816 8.23575 7.873 8.32725C7.93 8.41875 7.99525 8.48325 8.06875 8.52075C8.14375 8.55675 8.221 8.57475 8.3005 8.57475C8.3815 8.57475 8.4595 8.55675 8.5345 8.52075C8.6095 8.48325 8.6755 8.41875 8.7325 8.32725C8.791 8.23575 8.8375 8.11275 8.872 7.95825C8.9065 7.80375 8.92375 7.60875 8.92375 7.37325Z"
          fill="black"
        />
      </svg>
    </Box>
  );
}

function RotateMenuIcon180() {
  return (
    <Box as="span" display="inline-flex" flexShrink={0} aria-hidden>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M1.75 7C1.75 8.03835 2.05791 9.05339 2.63478 9.91674C3.21166 10.7801 4.0316 11.453 4.99091 11.8504C5.95022 12.2477 7.00582 12.3517 8.02422 12.1491C9.04262 11.9466 9.97808 11.4465 10.7123 10.7123C11.4465 9.97808 11.9465 9.04262 12.1491 8.02422C12.3517 7.00582 12.2477 5.95022 11.8504 4.99091C11.453 4.0316 10.7801 3.21166 9.91674 2.63478C9.05338 2.05791 8.03835 1.75 7 1.75C5.53 1.75 4.12416 2.33333 3.06833 3.34833L1.75 4.66667"
          stroke={ROTATE_MENU_NAVY}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M1.75033 1.75V4.66667H4.66699"
          stroke={ROTATE_MENU_NAVY}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.41322 8.58375V9H3.61322V8.58375H4.27247V6.687C4.27247 6.612 4.27472 6.53475 4.27922 6.45525L3.81122 6.84675C3.78422 6.86775 3.75722 6.88125 3.73022 6.88725C3.70472 6.89175 3.67997 6.89175 3.65597 6.88725C3.63347 6.88275 3.61322 6.87525 3.59522 6.86475C3.57722 6.85275 3.56372 6.84 3.55472 6.8265L3.37922 6.58575L4.37147 5.742H4.82822V8.58375H5.41322ZM6.99982 8.60175C7.09132 8.60175 7.17157 8.589 7.24057 8.5635C7.30957 8.5365 7.36732 8.49975 7.41382 8.45325C7.46182 8.40675 7.49782 8.35125 7.52182 8.28675C7.54582 8.22225 7.55782 8.1525 7.55782 8.0775C7.55782 7.899 7.50832 7.764 7.40932 7.6725C7.31032 7.581 7.17382 7.53525 6.99982 7.53525C6.82582 7.53525 6.68932 7.581 6.59032 7.6725C6.49132 7.764 6.44182 7.899 6.44182 8.0775C6.44182 8.1525 6.45382 8.22225 6.47782 8.28675C6.50182 8.35125 6.53707 8.40675 6.58357 8.45325C6.63157 8.49975 6.69007 8.5365 6.75907 8.5635C6.82807 8.589 6.90832 8.60175 6.99982 8.60175ZM6.99982 6.129C6.91732 6.129 6.84607 6.14175 6.78607 6.16725C6.72607 6.19275 6.67582 6.22725 6.63532 6.27075C6.59632 6.31275 6.56707 6.3615 6.54757 6.417C6.52957 6.4725 6.52057 6.531 6.52057 6.5925C6.52057 6.657 6.52807 6.72 6.54307 6.7815C6.55957 6.8415 6.58657 6.8955 6.62407 6.9435C6.66157 6.99 6.71107 7.0275 6.77257 7.056C6.83407 7.0845 6.90982 7.09875 6.99982 7.09875C7.08982 7.09875 7.16557 7.0845 7.22707 7.056C7.28857 7.0275 7.33807 6.99 7.37557 6.9435C7.41307 6.8955 7.43932 6.8415 7.45432 6.7815C7.47082 6.72 7.47907 6.657 7.47907 6.5925C7.47907 6.531 7.46932 6.4725 7.44982 6.417C7.43032 6.3615 7.40107 6.31275 7.36207 6.27075C7.32307 6.22725 7.27357 6.19275 7.21357 6.16725C7.15357 6.14175 7.08232 6.129 6.99982 6.129ZM7.54882 7.3035C7.74682 7.368 7.89307 7.467 7.98757 7.6005C8.08207 7.7325 8.12932 7.8945 8.12932 8.0865C8.12932 8.2305 8.10157 8.361 8.04607 8.478C7.99207 8.595 7.91557 8.69475 7.81657 8.77725C7.71757 8.85975 7.59832 8.9235 7.45882 8.9685C7.32082 9.0135 7.16782 9.036 6.99982 9.036C6.83182 9.036 6.67807 9.0135 6.53857 8.9685C6.40057 8.9235 6.28207 8.85975 6.18307 8.77725C6.08407 8.69475 6.00682 8.595 5.95132 8.478C5.89732 8.361 5.87032 8.2305 5.87032 8.0865C5.87032 7.8945 5.91757 7.7325 6.01207 7.6005C6.10657 7.467 6.25282 7.368 6.45082 7.3035C6.29332 7.2375 6.17482 7.14375 6.09532 7.02225C6.01732 6.90075 5.97832 6.7545 5.97832 6.5835C5.97832 6.4605 6.00307 6.34575 6.05257 6.23925C6.10357 6.13275 6.17407 6.0405 6.26407 5.9625C6.35407 5.883 6.46132 5.8215 6.58582 5.778C6.71182 5.733 6.84982 5.7105 6.99982 5.7105C7.14982 5.7105 7.28707 5.733 7.41157 5.778C7.53757 5.8215 7.64557 5.883 7.73557 5.9625C7.82557 6.0405 7.89532 6.13275 7.94482 6.23925C7.99582 6.34575 8.02132 6.4605 8.02132 6.5835C8.02132 6.7545 7.98157 6.90075 7.90207 7.02225C7.82407 7.14375 7.70632 7.2375 7.54882 7.3035ZM10.8027 7.37325C10.8027 7.65675 10.7719 7.9035 10.7104 8.1135C10.6504 8.322 10.5664 8.4945 10.4584 8.631C10.3519 8.7675 10.2252 8.8695 10.0782 8.937C9.93268 9.003 9.77518 9.036 9.60568 9.036C9.43618 9.036 9.27868 9.003 9.13318 8.937C8.98918 8.8695 8.86393 8.7675 8.75743 8.631C8.65093 8.4945 8.56768 8.322 8.50768 8.1135C8.44768 7.9035 8.41768 7.65675 8.41768 7.37325C8.41768 7.08825 8.44768 6.8415 8.50768 6.633C8.56768 6.4245 8.65093 6.252 8.75743 6.1155C8.86393 5.979 8.98918 5.87775 9.13318 5.81175C9.27868 5.74425 9.43618 5.7105 9.60568 5.7105C9.77518 5.7105 9.93268 5.74425 10.0782 5.81175C10.2252 5.87775 10.3519 5.979 10.4584 6.1155C10.5664 6.252 10.6504 6.4245 10.7104 6.633C10.7719 6.8415 10.8027 7.08825 10.8027 7.37325ZM10.2289 7.37325C10.2289 7.13775 10.2117 6.94275 10.1772 6.78825C10.1427 6.63375 10.0962 6.51075 10.0377 6.41925C9.98068 6.32775 9.91468 6.264 9.83968 6.228C9.76468 6.1905 9.68668 6.17175 9.60568 6.17175C9.52618 6.17175 9.44893 6.1905 9.37393 6.228C9.30043 6.264 9.23518 6.32775 9.17818 6.41925C9.12118 6.51075 9.07543 6.63375 9.04093 6.78825C9.00793 6.94275 8.99143 7.13775 8.99143 7.37325C8.99143 7.60875 9.00793 7.80375 9.04093 7.95825C9.07543 8.11275 9.12118 8.23575 9.17818 8.32725C9.23518 8.41875 9.30043 8.48325 9.37393 8.52075C9.44893 8.55675 9.52618 8.57475 9.60568 8.57475C9.68668 8.57475 9.76468 8.55675 9.83968 8.52075C9.91468 8.48325 9.98068 8.41875 10.0377 8.32725C10.0962 8.23575 10.1427 8.11275 10.1772 7.95825C10.2117 7.80375 10.2289 7.60875 10.2289 7.37325Z"
          fill="black"
        />
      </svg>
    </Box>
  );
}

function DocumentCardRotateMenu({
  onPick,
  actionMinClip
}: {
  onPick: (kind: DocumentGridRotateKind) => void;
  actionMinClip: string;
}) {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeIfOutside = (e: MouseEvent | TouchEvent) => {
      const el = wrapRef.current;
      if (el && !el.contains(e.target as Node)) {
        onClose();
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', closeIfOutside);
    document.addEventListener('touchstart', closeIfOutside, { passive: true });
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', closeIfOutside);
      document.removeEventListener('touchstart', closeIfOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  const handlePick = (kind: DocumentGridRotateKind) => {
    onPick(kind);
    onClose();
  };

  const rotateRowProps = {
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: 3,
    w: '100%',
    px: 4,
    py: 3,
    borderWidth: 0,
    borderRadius: 0,
    bg: 'transparent',
    cursor: 'pointer',
    textAlign: 'left' as const,
    _hover: { bg: '#F9FAFB' }
  };

  return (
    <Box
      ref={wrapRef}
      position="relative"
      flex="1 1 0"
      minW={'77px'}
      zIndex={isOpen ? 5 : undefined}
      display="flex"
      flexDirection="column"
      alignItems="stretch"
    >
      <Box
        as="button"
        type="button"
        {...{ [DOC_GRID_STOP_CLICK]: '' }}
        className="documents-grid-view__rotate-menu-trigger"
        display="flex"
        flexWrap="nowrap"
        alignItems="center"
        justifyContent="center"
        gap={2}
        w="100%"
        maxW="100%"
        borderRadius="8px"
        bg="#fff"
        color={ROTATE_MENU_NAVY}
        fontSize="sm"
        fontWeight="600"
        fontFamily={DOC_CARD_OVERLAY_FONT}
        borderWidth={0}
        cursor="pointer"
        lineHeight="20px"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Open rotate options"
        sx={{
          padding: '8px',
          minWidth: actionMinClip
        }}
        _hover={{ bg: '#F9FAFB' }}
        _active={{ bg: '#F3F4F6' }}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        <DocumentCardRotateIcon />
        <Text as="span" textAlign="center" whiteSpace="nowrap">
          Rotate
        </Text>
      </Box>
      {isOpen ? (
        <Box
          className="documents-grid-view__rotate-menu-panel"
          position="absolute"
          left={0}
          right={0}
          bottom="calc(100% + 8px)"
          w="100%"
          minW={0}
          p={0}
          borderWidth="1px"
          borderColor="#CECECE"
          borderRadius="12px"
          boxShadow="0 4px 24px rgba(12, 30, 70, 0.14)"
          bg="#FFFFFF"
          overflow="hidden"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          role="listbox"
          aria-label="Rotate document"
        >
          <Box
            as="button"
            type="button"
            {...rotateRowProps}
            borderBottomWidth="1px"
            borderColor="#E5E7EB"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handlePick('cw90');
            }}
          >
            <RotateMenuIconCw90 />
            <Text
              as="span"
              fontFamily={DOC_CARD_OVERLAY_FONT}
              fontSize="12px"
              fontWeight="500"
              color={ROTATE_MENU_NAVY}
            >
              Clockwise
            </Text>
          </Box>
          <Box
            as="button"
            type="button"
            {...rotateRowProps}
            borderBottomWidth="1px"
            borderColor="#E5E7EB"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handlePick('ccw90');
            }}
          >
            <RotateMenuIconCcw90 />
            <Text
              as="span"
              fontFamily={DOC_CARD_OVERLAY_FONT}
              fontSize="12px"
              fontWeight="500"
              color={ROTATE_MENU_NAVY}
            >
              Anti-clockwise
            </Text>
          </Box>
          <Box
            as="button"
            type="button"
            {...rotateRowProps}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handlePick('deg180');
            }}
          >
            <RotateMenuIcon180 />
            <Text
              as="span"
              fontFamily={DOC_CARD_OVERLAY_FONT}
              fontSize="12px"
              fontWeight="500"
              color={ROTATE_MENU_NAVY}
            >
              180 Degree
            </Text>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

function DocumentCardRejectIcon() {
  return (
    <Box as="span" display="inline-flex" color="#DC2626" flexShrink={0} aria-hidden>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path
          d="M18 6L6 18M6 6l12 12"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
}

function DocumentCardReviewDoubleCheckIcon({ stroke }: { stroke: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
    >
      <path
        d="M10.5003 3.5L4.08366 9.91667L1.16699 7"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.833 5.83301L8.45801 10.208L7.58301 9.33301"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DocumentCardReviewHoverControl({
  kind,
  onPick
}: {
  kind: DocumentCardHoverReviewKind;
  onPick?: () => void | Promise<void>;
}) {
  const isDone = kind === 'done';
  const tooltipLabel = isDone
    ? 'Unmark as reviewed'
    : 'Mark as reviewed';
  const [isLoading, setIsLoading] = useState(false);
  const commonFlex = {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    w: '36px',
    h: '36px',
    minW: '36px',
    boxSizing: 'border-box' as const
  };
  const handlePick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onPick || isLoading) return;
    setIsLoading(true);
    try {
      await onPick();
    } finally {
      setIsLoading(false);
    }
  };

  if (isDone) {
    return (
      <Tooltip hasArrow placement="top" label={tooltipLabel} color="black" bg="white">
        <Box
          as="button"
          type="button"
          {...{ [DOC_GRID_STOP_CLICK]: '' }}
          {...commonFlex}
          bg="#0C1E46"
          borderWidth="0"
          disabled={isLoading || !onPick}
          cursor={isLoading ? 'not-allowed' : (onPick ? 'pointer' : 'default')}
          aria-label="Mark as unreviewed"
          _hover={onPick ? { bg: '#102A63' } : undefined}
          _active={onPick ? { bg: '#0C1E46' } : undefined}
          onClick={handlePick}
        >
          {isLoading ? <Spinner size="sm" color="#FFFFFF" /> : <DocumentCardReviewDoubleCheckIcon stroke="#FFFFFF" />}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip hasArrow placement="top" label={tooltipLabel} color="black" bg="white">
      <Box
        as="button"
        type="button"
        {...{ [DOC_GRID_STOP_CLICK]: '' }}
        {...commonFlex}
        bg="#FFFFFF"
        borderWidth="2px"
        borderColor="#0C1E46"
        color="#0C1E46"
        disabled={isLoading || !onPick}
        cursor={isLoading ? 'not-allowed' : 'pointer'}
        aria-label="Mark as reviewed"
        _hover={{ bg: '#F9FAFB' }}
        _active={{ bg: '#F3F4F6' }}
        onClick={handlePick}
      >
        {isLoading ? <Spinner size="sm" color="#0C1E46" /> : <DocumentCardReviewDoubleCheckIcon stroke="currentColor" />}
      </Box>
    </Tooltip>
  );
}

function DocumentCardHoverHeadingBlock({
  hoverPrimaryLine,
  hoverSecondaryLine,
  hasStatusBadge
}: {
  hoverPrimaryLine?: string;
  hoverSecondaryLine?: string;
  hasStatusBadge: boolean;
}) {
  return (
    <Box
      textAlign="left"
      w="100%"
      minW={0}
    >
      {hoverPrimaryLine?.trim() ? (
        <Text
          fontFamily={DOC_CARD_OVERLAY_FONT}
          fontSize="14px"
          fontWeight="700"
          fontStyle="normal"
          lineHeight="normal"
          color="#FFF"
          wordBreak="break-word"
          overflowWrap="anywhere"
          noOfLines={3}
        >
          {hoverPrimaryLine.trim()}
        </Text>
      ) : null}
      {hoverSecondaryLine?.trim() ? (
        <Text
          mt={hoverPrimaryLine?.trim() ? 1 : 0}
          fontFamily={DOC_CARD_OVERLAY_FONT}
          fontSize="12px"
          fontWeight="400"
          fontStyle="normal"
          lineHeight="normal"
          color="#FFF"
          wordBreak="break-word"
          overflowWrap="anywhere"
          noOfLines={5}
        >
          {hoverSecondaryLine.trim()}
        </Text>
      ) : null}
    </Box>
  );
}

type DocumentGridCardProps<T> = {
  item: T;
  itemId: string;
  imagePath: string;
  /**
   * Increments when the S3 object at `imagePath` was overwritten in place (e.g. rotate)
   * so the card refetches; pair with `getFileBlob(..., { bypassHttpCache })` after reload.
   */
  imageReloadNonce?: number;
  cardWidthPx: number;
  statusBadge?: DocumentCardStatusBadgeKind;
  s3Bucket: string;
  onActivate: (item: T) => void;
  hoverOverlayEnabled?: boolean;
  hoverPrimaryLine?: string;
  hoverSecondaryLine?: string;
  /** When false, hover overlay omits Reject (e.g. already rejected). */
  showHoverReject: boolean;
  onHoverReject?: (item: T) => void;
  onHoverRotate?: (item: T, kind: DocumentGridRotateKind) => void;
  /** License-style gallery: pending = actionable review; reviewed = filled indicator. */
  hoverReviewKind?: DocumentCardHoverReviewKind;
  onHoverReview?: (item: T) => void;
};

function DocumentGridCard<T>({
  item,
  itemId,
  imagePath,
  imageReloadNonce = 0,
  cardWidthPx,
  statusBadge,
  s3Bucket,
  onActivate,
  hoverOverlayEnabled = false,
  hoverPrimaryLine,
  hoverSecondaryLine,
  showHoverReject,
  onHoverReject,
  onHoverRotate,
  hoverReviewKind,
  onHoverReview
}: DocumentGridCardProps<T>) {
  const path = imagePath;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const itemRef = useRef(item);
  const onActivateRef = useRef(onActivate);
  const onHoverRejectRef = useRef(onHoverReject);
  const onHoverRotateRef = useRef(onHoverRotate);
  const onHoverReviewRef = useRef(onHoverReview);
  const [inView, setInView] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [broken, setBroken] = useState(false);
  /** PDF grid card: fetch succeeded and blob looks like a PDF (no iframe preview — browser viewer fires onLoad before doc renders). */
  const [pdfFetchOk, setPdfFetchOk] = useState(false);
  const objectUrlRef = useRef<string | null>(null);
  const isPdf = Boolean(path?.toLowerCase().endsWith('.pdf'));
  /** Caller-supplied `blob:` / `data:` URL — not fetched from S3; caller owns revoke. */
  const isDirectDisplayPath = Boolean(
    path && (/^blob:/i.test(path) || /^data:/i.test(path))
  );

  itemRef.current = item;

  useEffect(() => {
    onActivateRef.current = onActivate;
  }, [onActivate]);
  useEffect(() => {
    onHoverRejectRef.current = onHoverReject;
  }, [onHoverReject]);
  useEffect(() => {
    onHoverRotateRef.current = onHoverRotate;
  }, [onHoverRotate]);
  useEffect(() => {
    onHoverReviewRef.current = onHoverReview;
  }, [onHoverReview]);

  const showHoverReviewControl =
    hoverReviewKind === 'done' ||
    (hoverReviewKind === 'action' && Boolean(onHoverReview));

  useLayoutEffect(() => {
    if (!path) {
      setInView(true);
      return;
    }
    if (isDirectDisplayPath) {
      setInView(true);
      return;
    }
    const el = rootRef.current;
    if (!el) {
      return;
    }
    setInView(false);
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin: '280px 0px', threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [path, isDirectDisplayPath]);

  useEffect(() => {
    let cancelled = false;
    if (!path) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setSrc(null);
      setPdfFetchOk(false);
      setLoading(false);
      return;
    }
    if (isDirectDisplayPath) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setLoading(false);
      setBroken(false);
      setPdfFetchOk(false);
      const p = path;
      let cancelDirect = false;
      const preload = new window.Image();
      preload.onload = () => {
        if (cancelDirect) return;
        runAfterImageDecode(preload, () => {
          if (cancelDirect) return;
          setSrc(p);
        });
      };
      preload.onerror = () => {
        if (!cancelDirect) setBroken(true);
      };
      preload.src = p;
      return () => {
        cancelDirect = true;
      };
    }
    if (!inView) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setBroken(false);
    setPdfFetchOk(false);
    (async () => {
      let rasterDecodePending = false;
      try {
        const storageKey = path.split('?')[0].split('#')[0];
        let blob = await limitThumbS3Fetch(() =>
          s3Utils.getFileBlob(storageKey, s3Bucket, { public: '' }, {
            bypassHttpCache: imageReloadNonce > 0
          })
        );
        const ext = storageKey.split('.').pop() as string;
        const contentType =
          mime.lookup(ext) ||
          (isPdf ? 'application/pdf' : 'application/octet-stream');
        blob = new Blob([blob], { type: contentType });
        if (cancelled) {
          return;
        }
        if (!(blob && blob.type)) {
          if (!cancelled) {
            setSrc(null);
            setBroken(true);
          }
          return;
        }
        if (isPdf) {
          const ok = await pdfBlobHasPdfHeader(blob);
          if (cancelled) {
            return;
          }
          if (!ok) {
            setSrc(null);
            setBroken(true);
            return;
          }
          setSrc(null);
          setPdfFetchOk(true);
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        rasterDecodePending = true;
        const preload = new window.Image();
        preload.onload = () => {
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            setLoading(false);
            return;
          }
          runAfterImageDecode(preload, () => {
            if (cancelled) {
              URL.revokeObjectURL(objectUrl);
              setLoading(false);
              return;
            }
            if (objectUrlRef.current && objectUrlRef.current !== objectUrl) {
              URL.revokeObjectURL(objectUrlRef.current);
            }
            objectUrlRef.current = objectUrl;
            setSrc(objectUrl);
            setLoading(false);
          });
        };
        preload.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          if (!cancelled) {
            setSrc(null);
            setBroken(true);
            setLoading(false);
          }
        };
        preload.src = objectUrl;
      } catch {
        if (!cancelled) {
          setSrc(null);
          setBroken(true);
        }
      } finally {
        if (!cancelled && !rasterDecodePending) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      // Do not revoke object URLs or clear `src` here. Clearing caused a flash of
      // placeholder + spinner when `imageReloadNonce` bumped after gallery rotate
      // (same S3 key, new bytes). The next successful fetch revokes the prior URL
      // before assigning the new one; blob URLs are released on card unmount below.
    };
  }, [path, isPdf, inView, s3Bucket, isDirectDisplayPath, imageReloadNonce]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  const showImageHoverChrome =
    hoverOverlayEnabled &&
    !broken &&
    (isPdf ? pdfFetchOk : Boolean(src));

  const hoverFooterActionMinClip = useMemo(
    () => docCardHoverActionMinClipExpr(cardWidthPx),
    [cardWidthPx]
  );

  const handleCardClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.target as HTMLElement;
    if (el.closest(`[${DOC_GRID_STOP_CLICK}]`)) {
      return;
    }
    onActivateRef.current(itemRef.current);
  }, []);

  const handleCardKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') {
        return;
      }
      if (e.target !== e.currentTarget) {
        return;
      }
      e.preventDefault();
      onActivateRef.current(itemRef.current);
    },
    []
  );

  return (
    <Box
      ref={rootRef}
      className="documents-grid-view__card"
      role="group"
      tabIndex={0}
      aria-label={`Open document ${itemId}`}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      w={`${cardWidthPx}px`}
      flexShrink={0}
      borderRadius="10px"
      border="2px solid #E5E7EB"
      bg="#FFF"
      boxShadow="0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10)"
      overflow="hidden"
      textAlign="left"
      cursor="pointer"
      position="relative"
      transition="box-shadow 0.15s ease"
      _hover={{
        boxShadow:
          '0 2px 6px 0 rgba(0, 0, 0, 0.12), 0 2px 4px -2px rgba(0, 0, 0, 0.10) !important',
          borderColor: 'none !important'
      }}
      style={{
        width: cardWidthPx,
        aspectRatio: `${THUMB_ASPECT_W} / ${THUMB_ASPECT_H}`
      }}
    >
      {statusBadge ? <DocumentCardStatusBadge kind={statusBadge} /> : null}
      {isPdf && !broken && pdfFetchOk ? (
        <Box
          w="100%"
          h="100%"
          position="relative"
          bg="gray.100"
          overflow="hidden"
          borderRadius="10px"
        >
          <Flex
            w="100%"
            h="100%"
            align="center"
            justify="center"
            pointerEvents="none"
          >
            <DocumentPdfPlaceholderIcon />
          </Flex>
          {showImageHoverChrome && (
            <Flex
              className="documents-grid-view__card-hover-overlay"
              position="absolute"
              inset={0}
              borderRadius="10px"
              direction="column"
              align="stretch"
              justify="space-between"
              justifyContent={'space-between'}
              p={3}
              bg={DOC_CARD_HOVER_OVERLAY_BG}
              zIndex={2}
            >
              <DocumentCardHoverHeadingBlock
                hoverPrimaryLine={hoverPrimaryLine}
                hoverSecondaryLine={hoverSecondaryLine}
                hasStatusBadge={Boolean(statusBadge)}
              />
              <Flex gap={2} w="100%" pt={2} mt="auto" align="center">
                <DocumentCardRotateMenu
                  actionMinClip={hoverFooterActionMinClip}
                  onPick={(kind) =>
                    onHoverRotateRef.current?.(itemRef.current, kind)
                  }
                />
                {showHoverReject ? (
                  <Box
                    as="button"
                    type="button"
                    {...{ [DOC_GRID_STOP_CLICK]: '' }}
                    flex="1 1 0"
                    minW={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                    borderRadius="8px"
                    bg="white"
                    color="#DC2626"
                    fontSize="12px"
                    fontWeight="600"
                    fontFamily={DOC_CARD_OVERLAY_FONT}
                    lineHeight="20px"
                    borderWidth="1px"
                    borderColor="#DC2626"
                    cursor="pointer"
                    sx={{
                      padding: DOC_CARD_HOVER_ACTION_PADDING,
                      minWidth: hoverFooterActionMinClip,
                      width: '100% !important'
                    }}
                    _hover={{ bg: 'red.50' }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onHoverRejectRef.current?.(itemRef.current);
                    }}
                    className="w-full"
                  >
                    <DocumentCardRejectIcon />
                    <Text as="span" textAlign="center" whiteSpace="nowrap">
                      Reject
                    </Text>
                  </Box>
                ) : null}
                {showHoverReviewControl && hoverReviewKind ? (
                  <DocumentCardReviewHoverControl
                    kind={hoverReviewKind}
                    onPick={() =>
                      onHoverReviewRef.current?.(itemRef.current)
                    }
                  />
                ) : null}
              </Flex>
            </Flex>
          )}
        </Box>
      ) : src && !broken && !isPdf ? (
        <Box
          w="100%"
          h="100%"
          position="relative"
          bg="gray.50"
          overflow="hidden"
          borderRadius="10px"
        >
          <Image
            src={thumbnailSrcForDisplay(src) ?? ''}
            alt=""
            w="100%"
            h="100%"
            objectFit="contain"
            borderRadius="10px"
            loading="eager"
            decoding="sync"
            onError={() => setBroken(true)}
          />
          {showImageHoverChrome && (
            <Flex
              className="documents-grid-view__card-hover-overlay"
              position="absolute"
              inset={0}
              borderRadius="10px"
              direction="column"
              align="stretch"
              justify="space-between"
              p={3}
              bg={DOC_CARD_HOVER_OVERLAY_BG}
              zIndex={2}
            >
              <DocumentCardHoverHeadingBlock
                hoverPrimaryLine={hoverPrimaryLine}
                hoverSecondaryLine={hoverSecondaryLine}
                hasStatusBadge={Boolean(statusBadge)}
              />
              <Flex gap={2} w="100%" pt={2} mt="auto" align="center">
                <DocumentCardRotateMenu
                  actionMinClip={hoverFooterActionMinClip}
                  onPick={(kind) =>
                    onHoverRotateRef.current?.(itemRef.current, kind)
                  }
                />
                {showHoverReject ? (
                  <Box
                    as="button"
                    type="button"
                    {...{ [DOC_GRID_STOP_CLICK]: '' }}
                    flex="1 1 0"
                    minW={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                    w="100%"
                    borderRadius="8px"
                    bg="white"
                    color="#DC2626"
                    fontSize="12px"
                    fontWeight="600"
                    fontFamily={DOC_CARD_OVERLAY_FONT}
                    lineHeight="20px"
                    borderWidth="1px"
                    borderColor="#DC2626"
                    cursor="pointer"
                    sx={{
                      padding: DOC_CARD_HOVER_ACTION_PADDING,
                      minWidth: hoverFooterActionMinClip
                    }}
                    _hover={{ bg: 'red.50' }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onHoverRejectRef.current?.(itemRef.current);
                    }}
                    className="w-full"
                  >
                    <DocumentCardRejectIcon />
                    <Text as="span" textAlign="center" whiteSpace="nowrap">
                      Reject
                    </Text>
                  </Box>
                ) : null}
                {showHoverReviewControl && hoverReviewKind ? (
                  <DocumentCardReviewHoverControl
                    kind={hoverReviewKind}
                    onPick={() =>
                      onHoverReviewRef.current?.(itemRef.current)
                    }
                  />
                ) : null}
              </Flex>
            </Flex>
          )}
        </Box>
      ) : (
        <Box
          w="100%"
          h="100%"
          p="10px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="relative"
          borderRadius="10px"
          overflow="hidden"
          {...PLACEHOLDER_INNER_SX}
        >
          {loading && (
            <Spinner size="md" color="#0C1E46" position="absolute" zIndex={2} />
          )}
          {isPdf ? <DocumentPdfPlaceholderIcon /> : <DocumentPlaceholderIcon />}
          {hoverOverlayEnabled ? (
            <Flex
              className="documents-grid-view__card-hover-overlay"
              position="absolute"
              inset={0}
              borderRadius="10px"
              direction="column"
              align="stretch"
              justify="space-between"
              p={3}
              bg={DOC_CARD_HOVER_OVERLAY_BG}
              zIndex={1}
            >
              <DocumentCardHoverHeadingBlock
                hoverPrimaryLine={hoverPrimaryLine}
                hoverSecondaryLine={hoverSecondaryLine}
                hasStatusBadge={Boolean(statusBadge)}
              />
              <Flex w="100%" mt="auto" pt={2} gap={2} align="center" minW={0}>
                {onHoverRotate ? (
                  <DocumentCardRotateMenu
                    actionMinClip={hoverFooterActionMinClip}
                    onPick={(kind) =>
                      onHoverRotateRef.current?.(itemRef.current, kind)
                    }
                  />
                ) : null}
                {showHoverReject ? (
                  <Box
                    as="button"
                    type="button"
                    {...{ [DOC_GRID_STOP_CLICK]: '' }}
                    flex="1 1 0"
                    minW={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                    borderRadius="8px"
                    bg="white"
                    color="#DC2626"
                    fontSize="sm"
                    fontWeight="600"
                    fontFamily={DOC_CARD_OVERLAY_FONT}
                    lineHeight="20px"
                    borderWidth="1px"
                    borderColor="#DC2626"
                    cursor="pointer"
                    sx={{
                      padding: DOC_CARD_HOVER_ACTION_PADDING,
                      minWidth: hoverFooterActionMinClip
                    }}
                    _hover={{ bg: 'red.50' }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onHoverRejectRef.current?.(itemRef.current);
                    }}
                    className="w-full"
                  >
                    <DocumentCardRejectIcon />
                    <Text as="span" textAlign="center" whiteSpace="nowrap">
                      Reject
                    </Text>
                  </Box>
                ) : null}
                {showHoverReviewControl && hoverReviewKind ? (
                  <DocumentCardReviewHoverControl
                    kind={hoverReviewKind}
                    onPick={() =>
                      onHoverReviewRef.current?.(itemRef.current)
                    }
                  />
                ) : null}
              </Flex>
            </Flex>
          ) : null}
        </Box>
      )}
    </Box>
  );
}

function cardsEqual<T>(
  prev: DocumentGridCardProps<T>,
  next: DocumentGridCardProps<T>
) {
  return (
    prev.itemId === next.itemId &&
    prev.imagePath === next.imagePath &&
    (prev.imageReloadNonce ?? 0) === (next.imageReloadNonce ?? 0) &&
    prev.cardWidthPx === next.cardWidthPx &&
    prev.statusBadge === next.statusBadge &&
    prev.s3Bucket === next.s3Bucket &&
    prev.hoverOverlayEnabled === next.hoverOverlayEnabled &&
    prev.hoverPrimaryLine === next.hoverPrimaryLine &&
    prev.hoverSecondaryLine === next.hoverSecondaryLine &&
    prev.showHoverReject === next.showHoverReject &&
    prev.onHoverReject === next.onHoverReject &&
    prev.onHoverRotate === next.onHoverRotate &&
    prev.hoverReviewKind === next.hoverReviewKind &&
    prev.onHoverReview === next.onHoverReview
  );
}

const MemoDocumentGridCardCompared = React.memo(
  DocumentGridCard,
  cardsEqual
) as typeof DocumentGridCard;

export type DocumentsGridViewProps<T> = {
  items: T[];
  totalCount: number;
  loading?: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  getItemId: (item: T) => string;
  getImagePath: (item: T) => string;
  /** Bust HTTP/browser cache for thumbnails after same-key S3 overwrite (e.g. rotate). */
  getImageReloadNonce?: (item: T) => number;
  /** Amplify / S3 bucket id (e.g. `masterFFLAssets`). */
  s3Bucket?: string;
  /**
   * Top-right status chip on each card (pending / rejected / approved).
   * When kind is `rejected`, hover Reject is hidden automatically.
   */
  getStatusBadge?: (item: T) => DocumentCardStatusBadgeKind | undefined;
  onItemClick?: (item: T) => void;
  /** When set, image cards show hover overlay (license line + entity + actions). */
  showDocumentCardHover?: boolean;
  getHoverPrimaryLine?: (item: T) => string | undefined;
  getHoverSecondaryLine?: (item: T) => string | undefined;
  onHoverReject?: (item: T) => void;
  onHoverRotate?: (item: T, kind: DocumentGridRotateKind) => void;
  getHoverReviewKind?: (item: T) => DocumentCardHoverReviewKind | undefined;
  onHoverReview?: (item: T) => void;
  /** When set, empty state matches table/grid `not-found-box` styling. */
  emptyTitle?: string;
  emptyDescription?: string;
  emptyMessage?: string;
  pageSizeOptions?: readonly number[];
  className?: string;
};

/**
 * Presentation-only document thumbnail grid: slider, cards, pagination.
 * Wire LOA/SOT/license by passing `items`, fetch in parent, and `getImagePath` / `s3Bucket`.
 */
export function DocumentsGridView<T>({
  items,
  totalCount,
  loading = false,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  getItemId,
  getImagePath,
  getImageReloadNonce,
  s3Bucket = 'masterFFLAssets',
  getStatusBadge,
  onItemClick,
  showDocumentCardHover = false,
  getHoverPrimaryLine,
  getHoverSecondaryLine,
  onHoverReject,
  onHoverRotate,
  getHoverReviewKind,
  onHoverReview,
  emptyTitle,
  emptyDescription,
  emptyMessage = 'No documents found for the current filters.',
  pageSizeOptions = DOCUMENTS_GRID_PAGE_SIZE_OPTIONS,
  className = 'documents-grid-view'
}: DocumentsGridViewProps<T>) {
  const [thumbSlider, setThumbSlider] = useState(0);
  const deferredThumbSlider = useDeferredValue(thumbSlider);
  const cardWidthPx = useMemo(
    () => lerp(THUMB_MIN_W, THUMB_MAX_W, deferredThumbSlider / 100),
    [deferredThumbSlider]
  );

  const onItemClickRef = useRef(onItemClick);
  useEffect(() => {
    onItemClickRef.current = onItemClick;
  }, [onItemClick]);

  const stableActivate = useCallback((item: T) => {
    onItemClickRef.current?.(item);
  }, []);

  const minPageSizeOption = useMemo(
    () => (pageSizeOptions.length ? Math.min(...pageSizeOptions) : 30),
    [pageSizeOptions]
  );
  /** No alternate page sizes matter when everything fits below the smallest option (e.g. under 30). */
  const isPageSizeSelectDisabled = totalCount < minPageSizeOption;

  const isThumbnailSliderDisabled = totalCount === 0;

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize) || 1);
  const safePage = Math.min(page, totalPages);
  const startItem =
    totalCount === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endItem =
    totalCount === 0
      ? 0
      : Math.min(safePage * pageSize, totalCount);

  useEffect(() => {
    if (page !== safePage) onPageChange(safePage);
  }, [page, safePage, onPageChange]);

  return (
    <Box
      className={className}
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      bg="white"
      overflow="hidden"
    >
      <Flex
        className="documents-grid-view__toolbar"
        align="center"
        justify="flex-end"
        gap={4}
        px={5}
        py={3}
        borderBottomWidth="1px"
        borderColor="gray.200"
        flexWrap="wrap"
      >
        <Flex align="center" gap={3}>
          <Text
            className="documents-grid-view__slider-label"
            color={isThumbnailSliderDisabled ? 'gray.400' : undefined}
          >
            Thumbnail Size:
          </Text>
          <input
            type="range"
            min={0}
            max={100}
            value={thumbSlider}
            onChange={(e) => setThumbSlider(Number(e.target.value))}
            className="documents-grid-view__slider"
            aria-label="Thumbnail size"
            disabled={isThumbnailSliderDisabled}
            style={
              { '--range-fill': `${thumbSlider}%` } as React.CSSProperties
            }
          />
        </Flex>
      </Flex>

      <Box
        className="documents-grid-view__grid-scroll"
        p={5}
        minH="320px"
        position="relative"
      >
        {loading && (
          <Flex
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            bg="whiteAlpha.800"
            zIndex={1}
          >
            <Spinner size="lg" color="#0C1E46" />
          </Flex>
        )}
        <Flex
          className="documents-grid-view__grid"
          flexWrap="wrap"
          gap={5}
          justify="flex-start"
        >
          {items.map((item) => (
            <MemoDocumentGridCardCompared
              key={getItemId(item)}
              item={item}
              itemId={getItemId(item)}
              imagePath={getImagePath(item)}
              imageReloadNonce={getImageReloadNonce?.(item) ?? 0}
              cardWidthPx={cardWidthPx}
              statusBadge={getStatusBadge?.(item)}
              s3Bucket={s3Bucket}
              onActivate={stableActivate}
              hoverOverlayEnabled={showDocumentCardHover}
              hoverPrimaryLine={getHoverPrimaryLine?.(item)}
              hoverSecondaryLine={getHoverSecondaryLine?.(item)}
              showHoverReject={
                Boolean(onHoverReject) &&
                getStatusBadge?.(item) !== 'rejected'
              }
              onHoverReject={onHoverReject}
              onHoverRotate={onHoverRotate}
              hoverReviewKind={getHoverReviewKind?.(item)}
              onHoverReview={onHoverReview}
            />
          ))}
        </Flex>
        {!loading && items.length === 0 &&
          (emptyTitle ? (
            <Flex
              as="div"
              className="not-found-box documents-grid-view__not-found"
              direction="column"
              align="center"
              justify="center"
              w="100%"
            >
              <img src="/images/ui/not-found-icon.png" alt="" />
              <p className="not-found-box__title">{emptyTitle}</p>
              <p className="not-found-box__message">
                {emptyDescription ?? GALLERY_EMPTY_FILTER_HINT}
              </p>
            </Flex>
          ) : (
            <Text
              fontSize="sm"
              color="gray.500"
              textAlign="center"
              py={10}
              whiteSpace="pre-line"
              px={4}
            >
              {emptyMessage}
            </Text>
          ))}
      </Box>

      <Flex
        className="documents-grid-view__pagination"
        align="center"
        justify="flex-end"
        gap={6}
        flexWrap="wrap"
        px={5}
        py={3}
        borderTopWidth="1px"
        borderColor="gray.200"
        fontSize="sm"
        color="gray.700"
      >
        <Flex align="center" gap={2}>
          <Text whiteSpace="nowrap">Page Size:</Text>
          <Select
            size="sm"
            w="88px"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            isDisabled={isPageSizeSelectDisabled}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </Flex>
        <Text whiteSpace="nowrap">
          {startItem} to {endItem} of {totalCount.toLocaleString()}
        </Text>
        <Flex align="center" gap={1}>
          <PaginationIconButton
            label="First page"
            onClick={() => onPageChange(1)}
            disabled={safePage <= 1}
          >
            {'|<'}
          </PaginationIconButton>
          <PaginationIconButton
            label="Previous page"
            onClick={() => onPageChange(Math.max(1, safePage - 1))}
            disabled={safePage <= 1}
          >
            {'<'}
          </PaginationIconButton>
          <Text px={2} whiteSpace="nowrap">
            Page {safePage} of {totalPages}
          </Text>
          <PaginationIconButton
            label="Next page"
            onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
            disabled={safePage >= totalPages}
          >
            {'>'}
          </PaginationIconButton>
          <PaginationIconButton
            label="Last page"
            onClick={() => onPageChange(totalPages)}
            disabled={safePage >= totalPages}
          >
            {'>|'}
          </PaginationIconButton>
        </Flex>
      </Flex>
    </Box>
  );
}

function PaginationIconButton({
  children,
  label,
  onClick,
  disabled
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Box
      as="button"
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      px={2}
      py={1}
      lineHeight={1}
      borderRadius="md"
      borderWidth="0"
      bg="transparent"
      color={disabled ? 'gray.300' : '#0C1E46'}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      fontSize="md"
      fontWeight="600"
      _hover={
        disabled
          ? undefined
          : { bg: 'blackAlpha.50' }
      }
    >
      {children}
    </Box>
  );
}

function isAgFilterShape(v: unknown): v is { filterType: string } {
  return Boolean(
    v && typeof v === 'object' && 'filterType' in (v as object)
  );
}

function buildLicenseGridFilterModel(licenseApprovalFilterModel: any) {
  const model: Record<string, unknown> = {};

  const put = (agField: string, reduxVal: unknown) => {
    if (isAgFilterShape(reduxVal)) model[agField] = reduxVal;
  };

  put('approval_status', licenseApprovalFilterModel?.status);
  put('error_code', licenseApprovalFilterModel?.faildSteps);
  put('is_external', licenseApprovalFilterModel?.storeType);
  put('source', licenseApprovalFilterModel?.source);
  put('searchText', licenseApprovalFilterModel?.searchText);

  const dt = licenseApprovalFilterModel?.created_date_time as
    | { filterType: string; dateFrom?: string; dateTo?: string }
    | undefined;
  if (
    dt &&
    isAgFilterShape(dt) &&
    dt.dateFrom &&
    dt.dateTo &&
    new Date(dt.dateFrom) <= new Date(dt.dateTo)
  ) {
    model.created_date_time = dt;
  }

  return model;
}

function buildLicenseGridContext(
  licenseApprovalFilterModel: any,
  hideUnnecessaryRejectionValue: boolean
) {
  const ctx: any = {
    customFilters: {
      hideRejection: !hideUnnecessaryRejectionValue
    }
  };
  if (licenseApprovalFilterModel?.isManuallyRequested) {
    ctx.customFilters.isManuallyRequested =
      licenseApprovalFilterModel?.isManuallyRequested?.values || [];
  }
  return ctx;
}

function buildSOTGridFilterModel(sotApprovalFilterModel: any) {
  const model: Record<string, unknown> = {};

  const put = (agField: string, reduxVal: unknown) => {
    if (isAgFilterShape(reduxVal)) model[agField] = reduxVal;
  };

  put('status', sotApprovalFilterModel?.status);
  put('error_code', sotApprovalFilterModel?.faildSteps);
  put('is_external', sotApprovalFilterModel?.storeType);
  put('source', sotApprovalFilterModel?.source);
  put('searchText', sotApprovalFilterModel?.searchText);

  const dt = sotApprovalFilterModel?.created_date_time as
    | { filterType: string; dateFrom?: string; dateTo?: string }
    | undefined;
  if (
    dt &&
    isAgFilterShape(dt) &&
    dt.dateFrom &&
    dt.dateTo &&
    new Date(dt.dateFrom) <= new Date(dt.dateTo)
  ) {
    model.created_date_time = dt;
  }

  return model;
}

function buildLOAGridFilterModel(loaApprovalFilterModel: any) {
  const model: Record<string, unknown> = {};

  const put = (agField: string, reduxVal: unknown) => {
    if (isAgFilterShape(reduxVal)) model[agField] = reduxVal;
  };

  put('status', loaApprovalFilterModel?.status);
  put('error_code', loaApprovalFilterModel?.faildSteps);
  put('is_external', loaApprovalFilterModel?.storeType);
  put('source', loaApprovalFilterModel?.source);
  put('searchText', loaApprovalFilterModel?.searchText);

  const dt = loaApprovalFilterModel?.created_date_time as
    | { filterType: string; dateFrom?: string; dateTo?: string }
    | undefined;
  if (
    dt &&
    isAgFilterShape(dt) &&
    dt.dateFrom &&
    dt.dateTo &&
    new Date(dt.dateFrom) <= new Date(dt.dateTo)
  ) {
    model.created_date_time = dt;
  }

  return model;
}

export interface LicenseDocumentsGridViewProps {
  /** When non-empty, uses client-side paging only (no certificate-history fetch). */
  docData?: LicenseReview[];
  onDocumentClick?: (doc: LicenseReview) => void;
  onHoverReject?: (doc: LicenseReview) => void;
  onHoverRotate?: (doc: LicenseReview, kind: DocumentGridRotateKind) => void;
  getHoverReviewKind?: (
    doc: LicenseReview
  ) => DocumentCardHoverReviewKind | undefined;
  onHoverReview?: (doc: LicenseReview) => void;
  /** Increment after server mutations (e.g. approve/reject) so gallery refetches while table grid may be unmounted. */
  refreshTrigger?: number;
  /**
   * Per-row `blob:` URLs to show after client-side rotate (caller must revoke on replace / unmount).
   * When set for `d.id`, overrides S3 thumbnail path for that card.
   */
  galleryThumbnailUrlByDocId?: Record<string, string>;
  /**
   * Per certificate id: incremented after in-place S3 image overwrite; persisted in
   * `sessionStorage` by the caller so a full page reload still refetches fresh bytes.
   */
  galleryThumbnailReloadNonceByDocId?: Record<string, number>;
}

/**
 * License Approval–specific grid: Redux filters + `LicenseReviewDataSource`.
 * For LOA/SOT, use `DocumentsGridView` with your own fetch and `getImagePath` / `s3Bucket`.
 */
export default function LicenseDocumentsGridView({
  docData,
  onDocumentClick,
  onHoverReject,
  onHoverRotate,
  getHoverReviewKind,
  onHoverReview,
  refreshTrigger = 0,
  galleryThumbnailUrlByDocId,
  galleryThumbnailReloadNonceByDocId
}: LicenseDocumentsGridViewProps) {
  const [searchParams] = useSearchParams();
  const licenseApprovalFilterModel = getLicenseApprovalFilterModel();
  const hideUnnecessaryRejectionValue = getHideUnnecessaryRejectionValue();
  const licenseApprovalSort = getLicenseApprovalSort();

  const dataSource = useMemo(() => new LicenseReviewDataSource(), []);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(
    SERVER_PAGE_LICENSE_APPROVAL_GALLERY_SIZE
  );
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState<LicenseReview[]>([]);
  const [loading, setLoading] = useState(false);

  const filterModel = useMemo(
    () => buildLicenseGridFilterModel(licenseApprovalFilterModel),
    [licenseApprovalFilterModel]
  );

  const context = useMemo(
    () =>
      buildLicenseGridContext(
        licenseApprovalFilterModel,
        hideUnnecessaryRejectionValue
      ),
    [licenseApprovalFilterModel, hideUnnecessaryRejectionValue]
  );

  useEffect(() => {
    dataSource.setSortBy(licenseApprovalSort === 'true');
  }, [dataSource, licenseApprovalSort]);

  const useExternalData = Boolean(docData && docData.length > 0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (useExternalData) {
        const all = docData!;
        const slice = all.slice(
          (page - 1) * pageSize,
          page * pageSize
        );
        if (!cancelled) {
          setRows(slice);
          setTotalCount(all.length);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const startRow = (page - 1) * pageSize;
        const result = await dataSource.fetchData({
          startRow,
          filterModel,
          context,
          pageSize
        });
        if (!cancelled) {
          setRows(result.items || []);
          setTotalCount(result.meta?.count ?? 0);
        }
      } catch {
        if (!cancelled) {
          setRows([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    context,
    dataSource,
    docData,
    filterModel,
    licenseApprovalSort,
    page,
    pageSize,
    useExternalData,
    refreshTrigger
  ]);

  // Reset page before the fetch effect runs so we do not call the API once with a stale
  // page number and again after setPage(1) (e.g. filters changed while on page 2+).
  useLayoutEffect(() => {
    setPage(1);
  }, [
    licenseApprovalFilterModel,
    hideUnnecessaryRejectionValue,
    licenseApprovalSort,
    searchParams,
    pageSize
  ]);

  const getLicenseGalleryImagePath = useCallback(
    (d: LicenseReview) => {
      const override = galleryThumbnailUrlByDocId?.[d.id]?.trim();
      if (override) return override;
      return d.unstamped_license_path || '';
    },
    [galleryThumbnailUrlByDocId]
  );

  const getLicenseGalleryImageReloadNonce = useCallback(
    (d: LicenseReview) => galleryThumbnailReloadNonceByDocId?.[d.id] ?? 0,
    [galleryThumbnailReloadNonceByDocId]
  );

  return (
    <DocumentsGridView<LicenseReview>
      items={rows}
      totalCount={totalCount}
      loading={loading}
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      getItemId={(d) => d.id}
      getImagePath={getLicenseGalleryImagePath}
      getImageReloadNonce={getLicenseGalleryImageReloadNonce}
      s3Bucket="masterFFLAssets"
      getStatusBadge={(d) => {
        if (d.reviewed_in_grid_by) return 'reviewed';
        const s = d.approval_status?.toLowerCase();
        if (s === 'pending') return 'pending';
        if (s === 'rejected') return 'rejected';
        if (s === 'approved') return 'approved';
        if (s === 'reviewed') return 'reviewed';
        return undefined;
      }}
      onItemClick={onDocumentClick}
      showDocumentCardHover
      getHoverPrimaryLine={(d) =>
        d.full_license_key?.trim() || d.license_key?.trim() || undefined
      }
      getHoverSecondaryLine={(d) =>
        d.business_name?.trim() ||
        d.printed_name?.trim() ||
        undefined
      }
      onHoverReject={onHoverReject}
      onHoverRotate={onHoverRotate}
      getHoverReviewKind={getHoverReviewKind}
      onHoverReview={onHoverReview}
      emptyTitle={LICENSE_GALLERY_EMPTY_TITLE}
      emptyDescription={GALLERY_EMPTY_FILTER_HINT}
    />
  );
}

export interface SOTDocumentsGridViewProps {
  docData?: SOTTaxStamps[];
  onDocumentClick?: (doc: SOTTaxStamps) => void;
  onHoverReject?: (doc: SOTTaxStamps) => void;
  onHoverRotate?: (doc: SOTTaxStamps, kind: DocumentGridRotateKind) => void;
  getHoverReviewKind?: (
    doc: SOTTaxStamps
  ) => DocumentCardHoverReviewKind | undefined;
  onHoverReview?: (doc: SOTTaxStamps) => void;
  refreshTrigger?: number;
  galleryThumbnailUrlByDocId?: Record<string, string>;
  galleryThumbnailReloadNonceByDocId?: Record<string, number>;
}

/**
 * SOT Approval gallery: Redux SOT filters + `upload-sot-list` via `SOTTaxStampsDataSource`.
 */
export function SOTDocumentsGridView({
  docData,
  onDocumentClick,
  onHoverReject,
  onHoverRotate,
  getHoverReviewKind,
  onHoverReview,
  refreshTrigger = 0,
  galleryThumbnailUrlByDocId,
  galleryThumbnailReloadNonceByDocId
}: SOTDocumentsGridViewProps) {
  const [searchParams] = useSearchParams();
  const sotApprovalFilterModel = getSOTApprovalFilterModel();
  const hideUnnecessarySOTRejectionValue = getHideUnnecessarySOTRejectionValue();
  const sotApprovalSort = getSOTApprovalSort();

  const dataSource = useMemo(() => new SOTTaxStampsDataSource(), []);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(
    SERVER_PAGE_LICENSE_APPROVAL_GALLERY_SIZE
  );
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState<SOTTaxStamps[]>([]);
  const [loading, setLoading] = useState(false);

  const filterModel = useMemo(
    () => buildSOTGridFilterModel(sotApprovalFilterModel),
    [sotApprovalFilterModel]
  );

  const context = useMemo(
    () =>
      buildLicenseGridContext(
        sotApprovalFilterModel,
        hideUnnecessarySOTRejectionValue
      ),
    [sotApprovalFilterModel, hideUnnecessarySOTRejectionValue]
  );

  useEffect(() => {
    dataSource.setSortBy(sotApprovalSort === 'true');
  }, [dataSource, sotApprovalSort]);

  const useExternalData = Boolean(docData && docData.length > 0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (useExternalData) {
        const all = docData!;
        const slice = all.slice(
          (page - 1) * pageSize,
          page * pageSize
        );
        if (!cancelled) {
          setRows(slice);
          setTotalCount(all.length);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const startRow = (page - 1) * pageSize;
        const result = await dataSource.fetchData({
          startRow,
          filterModel,
          context,
          pageSize
        });
        if (!cancelled) {
          setRows((result.items || []) as unknown as SOTTaxStamps[]);
          setTotalCount(result.meta?.count ?? 0);
        }
      } catch {
        if (!cancelled) {
          setRows([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    context,
    dataSource,
    docData,
    filterModel,
    page,
    pageSize,
    sotApprovalSort,
    useExternalData,
    refreshTrigger
  ]);

  useLayoutEffect(() => {
    setPage(1);
  }, [
    sotApprovalFilterModel,
    hideUnnecessarySOTRejectionValue,
    sotApprovalSort,
    searchParams,
    pageSize
  ]);

  const getSOTGalleryImagePath = useCallback(
    (d: SOTTaxStamps) => {
      const override = galleryThumbnailUrlByDocId?.[d.id]?.trim();
      if (override) return override;
      return d.license_path || '';
    },
    [galleryThumbnailUrlByDocId]
  );

  const getSOTGalleryImageReloadNonce = useCallback(
    (d: SOTTaxStamps) => galleryThumbnailReloadNonceByDocId?.[d.id] ?? 0,
    [galleryThumbnailReloadNonceByDocId]
  );

  return (
    <DocumentsGridView<SOTTaxStamps>
      items={rows}
      totalCount={totalCount}
      loading={loading}
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      getItemId={(d) => d.id}
      getImagePath={getSOTGalleryImagePath}
      getImageReloadNonce={getSOTGalleryImageReloadNonce}
      s3Bucket="sotTaxStamps"
      getStatusBadge={(d) => {
        if (d.reviewed_in_grid_by) return 'reviewed';
        const s = d.status?.toLowerCase();
        if (s === 'pending') return 'pending';
        if (s === 'rejected') return 'rejected';
        if (s === 'approved') return 'approved';
        if (s === 'reviewed') return 'reviewed';
        return undefined;
      }}
      onItemClick={onDocumentClick}
      showDocumentCardHover
      getHoverPrimaryLine={(d) =>
        d.full_license_key?.trim() || d.license_key?.trim() || undefined
      }
      getHoverSecondaryLine={(d) =>
        d.business_name?.trim() ||
        d.principal_business_name?.trim() ||
        undefined
      }
      onHoverReject={onHoverReject}
      onHoverRotate={onHoverRotate}
      getHoverReviewKind={getHoverReviewKind}
      onHoverReview={onHoverReview}
      emptyTitle={SOT_GALLERY_EMPTY_TITLE}
      emptyDescription={GALLERY_EMPTY_FILTER_HINT}
    />
  );
}

export interface LOADocumentsGridViewProps {
  docData?: LOAReview[];
  onDocumentClick?: (doc: LOAReview) => void;
  onHoverReject?: (doc: LOAReview) => void;
  onHoverRotate?: (doc: LOAReview, kind: DocumentGridRotateKind) => void;
  getHoverReviewKind?: (
    doc: LOAReview
  ) => DocumentCardHoverReviewKind | undefined;
  onHoverReview?: (doc: LOAReview) => void;
  refreshTrigger?: number;
  galleryThumbnailUrlByDocId?: Record<string, string>;
  galleryThumbnailReloadNonceByDocId?: Record<string, number>;
}

/**
 * LOA Approval gallery: Redux LOA filters + `upload-loa-list` via `LOATaxStampsDatasource`.
 */
export function LOADocumentsGridView({
  docData,
  onDocumentClick,
  onHoverReject,
  onHoverRotate,
  getHoverReviewKind,
  onHoverReview,
  refreshTrigger = 0,
  galleryThumbnailUrlByDocId,
  galleryThumbnailReloadNonceByDocId
}: LOADocumentsGridViewProps) {
  const [searchParams] = useSearchParams();
  const loaApprovalFilterModel = getLOAApprovalFilterModel();
  const hideUnnecessaryLOARejectionValue = getHideUnnecessaryLOARejectionValue();
  const loaApprovalSort = getLOAApprovalSort();

  const dataSource = useMemo(() => new LOATaxStampsDatasource(), []);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(
    SERVER_PAGE_LICENSE_APPROVAL_GALLERY_SIZE
  );
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState<LOAReview[]>([]);
  const [loading, setLoading] = useState(false);

  const filterModel = useMemo(
    () => buildLOAGridFilterModel(loaApprovalFilterModel),
    [loaApprovalFilterModel]
  );

  const context = useMemo(
    () =>
      buildLicenseGridContext(
        loaApprovalFilterModel,
        hideUnnecessaryLOARejectionValue
      ),
    [loaApprovalFilterModel, hideUnnecessaryLOARejectionValue]
  );

  useEffect(() => {
    dataSource.setSortBy(loaApprovalSort === 'true');
  }, [dataSource, loaApprovalSort]);

  const useExternalData = Boolean(docData && docData.length > 0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (useExternalData) {
        const all = docData!;
        const slice = all.slice(
          (page - 1) * pageSize,
          page * pageSize
        );
        if (!cancelled) {
          setRows(slice);
          setTotalCount(all.length);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const startRow = (page - 1) * pageSize;
        const result = await dataSource.fetchData({
          startRow,
          filterModel,
          context,
          pageSize
        });
        if (!cancelled) {
          setRows((result.items || []) as unknown as LOAReview[]);
          setTotalCount(result.meta?.count ?? 0);
        }
      } catch {
        if (!cancelled) {
          setRows([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    context,
    dataSource,
    docData,
    filterModel,
    loaApprovalSort,
    page,
    pageSize,
    useExternalData,
    refreshTrigger
  ]);

  useLayoutEffect(() => {
    setPage(1);
  }, [
    loaApprovalFilterModel,
    hideUnnecessaryLOARejectionValue,
    loaApprovalSort,
    searchParams,
    pageSize
  ]);

  const loaRowKey = (d: LOAReview) => String(d.id ?? '');

  const getLOAGalleryImagePath = useCallback(
    (d: LOAReview) => {
      const override = galleryThumbnailUrlByDocId?.[loaRowKey(d)]?.trim();
      if (override) return override;
      return d.path || '';
    },
    [galleryThumbnailUrlByDocId]
  );

  const getLOAGalleryImageReloadNonce = useCallback(
    (d: LOAReview) => galleryThumbnailReloadNonceByDocId?.[loaRowKey(d)] ?? 0,
    [galleryThumbnailReloadNonceByDocId]
  );

  return (
    <DocumentsGridView<LOAReview>
      items={rows}
      totalCount={totalCount}
      loading={loading}
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      getItemId={loaRowKey}
      getImagePath={getLOAGalleryImagePath}
      getImageReloadNonce={getLOAGalleryImageReloadNonce}
      s3Bucket="sotTaxStamps"
      getStatusBadge={(d) => {
        if (d.reviewed_in_grid_by) return 'reviewed';
        const s = d.status?.toLowerCase();
        if (s === 'pending') return 'pending';
        if (s === 'rejected') return 'rejected';
        if (s === 'approved') return 'approved';
        if (s === 'reviewed') return 'reviewed';
        return undefined;
      }}
      onItemClick={onDocumentClick}
      showDocumentCardHover
      getHoverPrimaryLine={(d) =>
        d.full_license_key?.trim() || d.license_key?.trim() || undefined
      }
      getHoverSecondaryLine={(d) => d.business_name?.trim() || undefined}
      onHoverReject={onHoverReject}
      onHoverRotate={onHoverRotate}
      getHoverReviewKind={getHoverReviewKind}
      onHoverReview={onHoverReview}
      emptyTitle={LOA_GALLERY_EMPTY_TITLE}
      emptyDescription={GALLERY_EMPTY_FILTER_HINT}
    />
  );
}
